/**
 * Generated by @openapi-codegen
 *
 * @version 1.0.0
 */
import * as reactQuery from 'app/src/shared/helpers/reactquery'
import { MembershipContext, useMembershipContext } from './membership-context'
import type * as Fetcher from './membership-fetcher'
import { membershipFetch } from './membership-fetcher'
import type * as Responses from './membership-responses'
import type * as Schemas from './membership-schemas'

export type MembershipGetPathParams = {
  /**
   * @maxLength 36
   */
  membershipId: string
}

export type MembershipGetQueryParams = {
  includeFamilyMembers?: boolean
  includePrizeGroups?: boolean
  includeAdditionalInfo?: boolean
}

export type MembershipGetHeaders = {
  /**
   * The identifier of the tenant
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
}

export type MembershipGetError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: Responses.ErrorBadRequest
    }
  | {
      status: 403
      payload: Responses.ErrorForbidden
    }
  | {
      status: 404
      payload: Responses.ErrorEntityNotFound
    }
>

export type MembershipGetVariables = {
  headers?: MembershipGetHeaders
  pathParams: MembershipGetPathParams
  queryParams?: MembershipGetQueryParams
} & MembershipContext['fetcherOptions']

/**
 * Returns the details of the Membership
 */
export const fetchMembershipGet = (variables: MembershipGetVariables, signal?: AbortSignal) =>
  membershipFetch<
    Schemas.MembershipExtended,
    MembershipGetError,
    undefined,
    MembershipGetHeaders,
    MembershipGetQueryParams,
    MembershipGetPathParams
  >({
    url: '/memberships/{membershipId}',
    method: 'get',
    ...variables,
    signal,
  })

/**
 * Returns the details of the Membership
 */
export const useMembershipGet = <TData = Schemas.MembershipExtended>(
  variables: MembershipGetVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.MembershipExtended, MembershipGetError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useMembershipContext(options)
  return reactQuery.useQuery<Schemas.MembershipExtended, MembershipGetError, TData>({
    queryKey: queryKeyFn({
      path: '/memberships/{membershipId}',
      operationId: 'membershipGet',
      variables,
    }),
    queryFn: ({ signal }) => fetchMembershipGet({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  })
}

export type MembershipPatchPathParams = {
  /**
   * @maxLength 36
   */
  membershipId: string
}

export type MembershipPatchHeaders = {
  /**
   * The identifier of the tenant
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
  /**
   * The ID of the connected operator
   */
  ['X-Operator']?: string
}

export type MembershipPatchError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: Responses.ErrorBadRequest
    }
  | {
      status: 403
      payload: Responses.ErrorForbidden
    }
  | {
      status: 404
      payload: Responses.ErrorEntityNotFound
    }
  | {
      status: 410
      payload: Responses.ErrorEntityExpired
    }
>

export type MembershipPatchVariables = {
  body: Schemas.MembershipUpdateRequest
  headers?: MembershipPatchHeaders
  pathParams: MembershipPatchPathParams
} & MembershipContext['fetcherOptions']

/**
 * Update membership request
 */
export const fetchMembershipPatch = (variables: MembershipPatchVariables, signal?: AbortSignal) =>
  membershipFetch<
    undefined,
    MembershipPatchError,
    Schemas.MembershipUpdateRequest,
    MembershipPatchHeaders,
    {},
    MembershipPatchPathParams
  >({
    url: '/memberships/{membershipId}',
    method: 'patch',
    ...variables,
    signal,
  })

/**
 * Update membership request
 */
export const useMembershipPatch = (
  options?: Omit<reactQuery.UseMutationOptions<undefined, MembershipPatchError, MembershipPatchVariables>, 'mutationFn'>,
) => {
  const { fetcherOptions } = useMembershipContext()
  return reactQuery.useMutation<undefined, MembershipPatchError, MembershipPatchVariables>({
    mutationFn: (variables: MembershipPatchVariables) => fetchMembershipPatch({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type UserMembershipListPathParams = {
  /**
   * @maxLength 255
   */
  userId: string
}

export type UserMembershipListQueryParams = {
  /**
   * Position of the first element to return
   *
   * @format int32
   * @default 0
   * @minimum 0
   */
  pagingOffset?: number
  /**
   * Maximum number of rows to return
   *
   * @format int32
   * @default 20
   * @minimum 1
   */
  pagingLimit?: number
}

export type UserMembershipListHeaders = {
  /**
   * The identifier of the tenant
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
  /**
   * The ID of the connected operator
   */
  ['X-Operator']?: string
}

export type UserMembershipListError = Fetcher.ErrorWrapper<
  | {
      status: 403
      payload: Responses.ErrorForbidden
    }
  | {
      status: 404
      payload: Responses.ErrorEntityNotFound
    }
>

export type UserMembershipListVariables = {
  headers?: UserMembershipListHeaders
  pathParams: UserMembershipListPathParams
  queryParams?: UserMembershipListQueryParams
} & MembershipContext['fetcherOptions']

/**
 * Returns the details of the Memberships of the given user
 */
export const fetchUserMembershipList = (variables: UserMembershipListVariables, signal?: AbortSignal) =>
  membershipFetch<
    Schemas.MembershipPaginated,
    UserMembershipListError,
    undefined,
    UserMembershipListHeaders,
    UserMembershipListQueryParams,
    UserMembershipListPathParams
  >({
    url: '/memberships/users/{userId}',
    method: 'get',
    ...variables,
    signal,
  })

/**
 * Returns the details of the Memberships of the given user
 */
export const useUserMembershipList = <TData = Schemas.MembershipPaginated>(
  variables: UserMembershipListVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.MembershipPaginated, UserMembershipListError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useMembershipContext(options)
  return reactQuery.useQuery<Schemas.MembershipPaginated, UserMembershipListError, TData>({
    queryKey: queryKeyFn({
      path: '/memberships/users/{userId}',
      operationId: 'userMembershipList',
      variables,
    }),
    queryFn: ({ signal }) => fetchUserMembershipList({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  })
}

export type UserMembershipActiveGetPathParams = {
  /**
   * @maxLength 255
   */
  userId: string
}

export type UserMembershipActiveGetQueryParams = {
  includeFamilyMembers?: boolean
  includeDocuments?: boolean
  includePrizeGroups?: boolean
  includeAdditionalInfo?: boolean
}

export type UserMembershipActiveGetHeaders = {
  /**
   * The identifier of the tenant
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
  /**
   * The ID of the connected operator
   */
  ['X-Operator']?: string
}

export type UserMembershipActiveGetError = Fetcher.ErrorWrapper<
  | {
      status: 403
      payload: Responses.ErrorForbidden
    }
  | {
      status: 404
      payload: Responses.ErrorEntityNotFound
    }
>

export type UserMembershipActiveGetVariables = {
  headers?: UserMembershipActiveGetHeaders
  pathParams: UserMembershipActiveGetPathParams
  queryParams?: UserMembershipActiveGetQueryParams
} & MembershipContext['fetcherOptions']

/**
 * Returns the details of the active Membership of the given user
 */
export const fetchUserMembershipActiveGet = (variables: UserMembershipActiveGetVariables, signal?: AbortSignal) =>
  membershipFetch<
    Schemas.Membership,
    UserMembershipActiveGetError,
    undefined,
    UserMembershipActiveGetHeaders,
    UserMembershipActiveGetQueryParams,
    UserMembershipActiveGetPathParams
  >({
    url: '/memberships/users/{userId}/active',
    method: 'get',
    ...variables,
    signal,
  })

/**
 * Returns the details of the active Membership of the given user
 */
export const useUserMembershipActiveGet = <TData = Schemas.Membership>(
  variables: UserMembershipActiveGetVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Membership, UserMembershipActiveGetError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useMembershipContext(options)
  return reactQuery.useQuery<Schemas.Membership, UserMembershipActiveGetError, TData>({
    queryKey: queryKeyFn({
      path: '/memberships/users/{userId}/active',
      operationId: 'userMembershipActiveGet',
      variables,
    }),
    queryFn: ({ signal }) => fetchUserMembershipActiveGet({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  })
}

export type MembershipJoinPathParams = {
  /**
   * @maxLength 36
   */
  membershipId: string
}

export type MembershipJoinHeaders = {
  /**
   * The identifier of the tenant
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
}

export type MembershipJoinError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: Responses.ErrorBadRequest
    }
  | {
      status: 403
      payload: Responses.ErrorForbidden
    }
  | {
      status: 404
      payload: Responses.ErrorEntityNotFound
    }
  | {
      status: 409
      payload: Responses.ErrorConflict
    }
  | {
      status: 410
      payload: Responses.ErrorEntityExpired
    }
>

export type MembershipJoinVariables = {
  body: Schemas.MembershipJoinRequest
  headers?: MembershipJoinHeaders
  pathParams: MembershipJoinPathParams
} & MembershipContext['fetcherOptions']

/**
 * Atomically sets the user's welfare percentage choice and acknowledges the required documents in a single transaction. Can be called multiple times to update the percentage. At least one document must be acknowledged per request.
 */
export const fetchMembershipJoin = (variables: MembershipJoinVariables, signal?: AbortSignal) =>
  membershipFetch<
    undefined,
    MembershipJoinError,
    Schemas.MembershipJoinRequest,
    MembershipJoinHeaders,
    {},
    MembershipJoinPathParams
  >({
    url: '/memberships/{membershipId}/join',
    method: 'put',
    ...variables,
    signal,
  })

/**
 * Atomically sets the user's welfare percentage choice and acknowledges the required documents in a single transaction. Can be called multiple times to update the percentage. At least one document must be acknowledged per request.
 */
export const useMembershipJoin = (
  options?: Omit<reactQuery.UseMutationOptions<undefined, MembershipJoinError, MembershipJoinVariables>, 'mutationFn'>,
) => {
  const { fetcherOptions } = useMembershipContext()
  return reactQuery.useMutation<undefined, MembershipJoinError, MembershipJoinVariables>({
    mutationFn: (variables: MembershipJoinVariables) => fetchMembershipJoin({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type UserFamilyMembersGetPathParams = {
  /**
   * @maxLength 36
   */
  membershipId: string
}

export type UserFamilyMembersGetHeaders = {
  /**
   * The identifier of the tenant
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
  /**
   * The ID of the connected operator
   */
  ['X-Operator']?: string
}

export type UserFamilyMembersGetError = Fetcher.ErrorWrapper<
  | {
      status: 403
      payload: Responses.ErrorForbidden
    }
  | {
      status: 404
      payload: Responses.ErrorEntityNotFound
    }
>

export type UserFamilyMembersGetResponse = {
  familyMemberId: string
  /**
   * @pattern ^[A-Za-zÀ-ÖØ-öø-ÿ][A-Za-zÀ-ÖØ-öø-ÿ' ]{1,}$
   * @example Mario
   */
  firstName?: string
  /**
   * @pattern ^[A-Za-zÀ-ÖØ-öø-ÿ][A-Za-zÀ-ÖØ-öø-ÿ' ]{1,}$
   * @example Rossi
   */
  lastName: string
  /**
   * @pattern ^[A-Za-z]{6}[0-9LMNPQRSTUVlmnpqrstuv]{2}[AEBHCLDMPRSTabcdehlmprst]{1}[0-9LMNPQRSTUVlmnpqrstuv]{2}[A-Za-z]{1}[0-9LMNPQRSTUVlmnpqrstuv]{3}[A-Za-z]{1}$
   * @example RSSMRA04A03H501N
   */
  fiscalCode: string
  /**
   * @example true
   */
  taxDependent: boolean
  /**
   * Enumerazione dei gradi di parentela
   *
   * @example coniuge
   */
  relationship:
    | 'coniuge'
    | 'fratello_sorella'
    | 'figlio_figlia'
    | 'genero_nuora'
    | 'suocero_suocera'
    | 'genitore'
    | 'genitore_convivente'
    | 'genitore_percettore'
    | 'ascendente'
    | 'figlio_figlia_coniuge_deceduto'
    | 'discendente_prossimo'
  allowEditing?: boolean
  privacyDocument?: {
    documentId: string
    documentName: string
    status: 'created' | 'uploaded'
    urlPath: string
  }
  signature: {
    signature: string
    /**
     * @format int64
     */
    validUntil: number
  }
}[]

export type UserFamilyMembersGetVariables = {
  headers?: UserFamilyMembersGetHeaders
  pathParams: UserFamilyMembersGetPathParams
} & MembershipContext['fetcherOptions']

/**
 * Returns the details of the active Membership of the given user
 */
export const fetchUserFamilyMembersGet = (variables: UserFamilyMembersGetVariables, signal?: AbortSignal) =>
  membershipFetch<
    UserFamilyMembersGetResponse,
    UserFamilyMembersGetError,
    undefined,
    UserFamilyMembersGetHeaders,
    {},
    UserFamilyMembersGetPathParams
  >({
    url: '/memberships/{membershipId}/familyMembers',
    method: 'get',
    ...variables,
    signal,
  })

/**
 * Returns the details of the active Membership of the given user
 */
export const useUserFamilyMembersGet = <TData = UserFamilyMembersGetResponse>(
  variables: UserFamilyMembersGetVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<UserFamilyMembersGetResponse, UserFamilyMembersGetError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useMembershipContext(options)
  return reactQuery.useQuery<UserFamilyMembersGetResponse, UserFamilyMembersGetError, TData>({
    queryKey: queryKeyFn({
      path: '/memberships/{membershipId}/familyMembers',
      operationId: 'userFamilyMembersGet',
      variables,
    }),
    queryFn: ({ signal }) => fetchUserFamilyMembersGet({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  })
}

export type UserFamilyMembersCreatePathParams = {
  /**
   * @maxLength 36
   */
  membershipId: string
}

export type UserFamilyMembersCreateHeaders = {
  /**
   * The identifier of the tenant
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
  /**
   * The ID of the connected operator
   */
  ['X-Operator']?: string
}

export type UserFamilyMembersCreateError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: Responses.ErrorBadRequest
    }
  | {
      status: 403
      payload: Responses.ErrorForbidden
    }
  | {
      status: 404
      payload: Responses.ErrorEntityNotFound
    }
  | {
      status: 409
      payload: Responses.ErrorConflict
    }
>

export type UserFamilyMembersCreateRequestBody = {
  familyMemberId: string
  /**
   * @pattern ^[A-Za-zÀ-ÖØ-öø-ÿ][A-Za-zÀ-ÖØ-öø-ÿ' ]{1,}$
   * @example Mario
   */
  firstName: string
  /**
   * @pattern ^[A-Za-zÀ-ÖØ-öø-ÿ][A-Za-zÀ-ÖØ-öø-ÿ' ]{1,}$
   * @example Rossi
   */
  lastName: string
  /**
   * @pattern ^[A-Za-z]{6}[0-9LMNPQRSTUVlmnpqrstuv]{2}[AEBHCLDMPRSTabcdehlmprst]{1}[0-9LMNPQRSTUVlmnpqrstuv]{2}[A-Za-z]{1}[0-9LMNPQRSTUVlmnpqrstuv]{3}[A-Za-z]{1}$
   * @example RSSMRA04A03H501N
   */
  fiscalCode: string
  /**
   * @example true
   */
  taxDependent: boolean
  /**
   * Enumerazione dei gradi di parentela
   *
   * @example coniuge
   */
  relationship:
    | 'coniuge'
    | 'fratello_sorella'
    | 'figlio_figlia'
    | 'genero_nuora'
    | 'suocero_suocera'
    | 'genitore'
    | 'genitore_convivente'
    | 'genitore_percettore'
    | 'ascendente'
    | 'figlio_figlia_coniuge_deceduto'
    | 'discendente_prossimo'
  allowEditing?: boolean
  privacyDocument?: {
    documentId: string
    documentName: string
    status: 'created' | 'uploaded'
    urlPath: string
  }
}

export type UserFamilyMembersCreateVariables = {
  body: UserFamilyMembersCreateRequestBody
  headers?: UserFamilyMembersCreateHeaders
  pathParams: UserFamilyMembersCreatePathParams
} & MembershipContext['fetcherOptions']

/**
 * Create family member data
 */
export const fetchUserFamilyMembersCreate = (variables: UserFamilyMembersCreateVariables, signal?: AbortSignal) =>
  membershipFetch<
    Schemas.FamilyMemberId,
    UserFamilyMembersCreateError,
    UserFamilyMembersCreateRequestBody,
    UserFamilyMembersCreateHeaders,
    {},
    UserFamilyMembersCreatePathParams
  >({
    url: '/memberships/{membershipId}/familyMembers',
    method: 'post',
    ...variables,
    signal,
  })

/**
 * Create family member data
 */
export const useUserFamilyMembersCreate = (
  options?: Omit<
    reactQuery.UseMutationOptions<Schemas.FamilyMemberId, UserFamilyMembersCreateError, UserFamilyMembersCreateVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useMembershipContext()
  return reactQuery.useMutation<Schemas.FamilyMemberId, UserFamilyMembersCreateError, UserFamilyMembersCreateVariables>({
    mutationFn: (variables: UserFamilyMembersCreateVariables) =>
      fetchUserFamilyMembersCreate({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type UserCheckHeaders = {
  /**
   * The identifier of the tenant
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
  /**
   * The identifier of the tenant
   */
  ['X-SelectedMembership']?: string
}

export type UserCheckError = Fetcher.ErrorWrapper<{
  status: 403
  payload: Responses.ErrorForbidden
}>

export type UserCheckVariables = {
  headers?: UserCheckHeaders
} & MembershipContext['fetcherOptions']

/**
 * Checks if the user is authorized to access resources
 */
export const fetchUserCheck = (variables: UserCheckVariables, signal?: AbortSignal) =>
  membershipFetch<Schemas.UserCheck, UserCheckError, undefined, UserCheckHeaders, {}, {}>({
    url: '/users/check',
    method: 'get',
    ...variables,
    signal,
  })

/**
 * Checks if the user is authorized to access resources
 */
export const useUserCheck = <TData = Schemas.UserCheck>(
  variables: UserCheckVariables,
  options?: Omit<reactQuery.UseQueryOptions<Schemas.UserCheck, UserCheckError, TData>, 'queryKey' | 'queryFn' | 'initialData'>,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useMembershipContext(options)
  return reactQuery.useQuery<Schemas.UserCheck, UserCheckError, TData>({
    queryKey: queryKeyFn({
      path: '/users/check',
      operationId: 'userCheck',
      variables,
    }),
    queryFn: ({ signal }) => fetchUserCheck({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  })
}

export type UserInfoHeaders = {
  /**
   * The identifier of the tenant
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
}

export type UserInfoError = Fetcher.ErrorWrapper<
  | {
      status: 401
      payload: Responses.ErrorUnauthorized
    }
  | {
      status: 403
      payload: Responses.ErrorForbidden
    }
>

export type UserInfoVariables = {
  headers?: UserInfoHeaders
} & MembershipContext['fetcherOptions']

/**
 * Returns the user info
 */
export const fetchUserInfo = (variables: UserInfoVariables, signal?: AbortSignal) =>
  membershipFetch<Schemas.UserInfo, UserInfoError, undefined, UserInfoHeaders, {}, {}>({
    url: '/users/info',
    method: 'get',
    ...variables,
    signal,
  })

/**
 * Returns the user info
 */
export const useUserInfo = <TData = Schemas.UserInfo>(
  variables: UserInfoVariables,
  options?: Omit<reactQuery.UseQueryOptions<Schemas.UserInfo, UserInfoError, TData>, 'queryKey' | 'queryFn' | 'initialData'>,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useMembershipContext(options)
  return reactQuery.useQuery<Schemas.UserInfo, UserInfoError, TData>({
    queryKey: queryKeyFn({
      path: '/users/info',
      operationId: 'userInfo',
      variables,
    }),
    queryFn: ({ signal }) => fetchUserInfo({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  })
}

export type UserSelfRegistrationHeaders = {
  /**
   * The identifier of the tenant
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
}

export type UserSelfRegistrationError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: Responses.ErrorBadRequest
    }
  | {
      status: 403
      payload: Responses.ErrorForbidden
    }
  | {
      status: 404
      payload: Responses.ErrorEntityNotFound
    }
  | {
      status: 409
      payload: Responses.ErrorConflict
    }
>

export type UserSelfRegistrationVariables = {
  body: Schemas.UserSelfRegistration
  headers?: UserSelfRegistrationHeaders
} & MembershipContext['fetcherOptions']

/**
 * Self registration of the user
 */
export const fetchUserSelfRegistration = (variables: UserSelfRegistrationVariables, signal?: AbortSignal) =>
  membershipFetch<undefined, UserSelfRegistrationError, Schemas.UserSelfRegistration, UserSelfRegistrationHeaders, {}, {}>({
    url: '/users/self-registration',
    method: 'post',
    ...variables,
    signal,
  })

/**
 * Self registration of the user
 */
export const useUserSelfRegistration = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, UserSelfRegistrationError, UserSelfRegistrationVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useMembershipContext()
  return reactQuery.useMutation<undefined, UserSelfRegistrationError, UserSelfRegistrationVariables>({
    mutationFn: (variables: UserSelfRegistrationVariables) => fetchUserSelfRegistration({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type UserSelfRegistrationConfigHeaders = {
  /**
   * The identifier of the tenant
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
}

export type UserSelfRegistrationConfigError = Fetcher.ErrorWrapper<{
  status: 403
  payload: Responses.ErrorForbidden
}>

export type UserSelfRegistrationConfigVariables = {
  headers?: UserSelfRegistrationConfigHeaders
} & MembershipContext['fetcherOptions']

/**
 * Get self registration configuration of the user
 */
export const fetchUserSelfRegistrationConfig = (variables: UserSelfRegistrationConfigVariables, signal?: AbortSignal) =>
  membershipFetch<
    Schemas.UserSelfRegistrationConfig,
    UserSelfRegistrationConfigError,
    undefined,
    UserSelfRegistrationConfigHeaders,
    {},
    {}
  >({
    url: '/users/self-registration-configuration',
    method: 'get',
    ...variables,
    signal,
  })

/**
 * Get self registration configuration of the user
 */
export const useUserSelfRegistrationConfig = <TData = Schemas.UserSelfRegistrationConfig>(
  variables: UserSelfRegistrationConfigVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.UserSelfRegistrationConfig, UserSelfRegistrationConfigError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useMembershipContext(options)
  return reactQuery.useQuery<Schemas.UserSelfRegistrationConfig, UserSelfRegistrationConfigError, TData>({
    queryKey: queryKeyFn({
      path: '/users/self-registration-configuration',
      operationId: 'userSelfRegistrationConfig',
      variables,
    }),
    queryFn: ({ signal }) => fetchUserSelfRegistrationConfig({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  })
}

export type UserFamilyMembersUpdatePathParams = {
  /**
   * @maxLength 255
   */
  familyMembersId: string
}

export type UserFamilyMembersUpdateHeaders = {
  /**
   * The identifier of the tenant
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
  /**
   * The ID of the connected operator
   */
  ['X-Operator']?: string
}

export type UserFamilyMembersUpdateError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: Responses.ErrorBadRequest
    }
  | {
      status: 403
      payload: Responses.ErrorForbidden
    }
  | {
      status: 404
      payload: Responses.ErrorEntityNotFound
    }
>

export type UserFamilyMembersUpdateRequestBody = {
  familyMemberId: string
  /**
   * @pattern ^[A-Za-zÀ-ÖØ-öø-ÿ][A-Za-zÀ-ÖØ-öø-ÿ' ]{1,}$
   * @example Mario
   */
  firstName: string
  /**
   * @pattern ^[A-Za-zÀ-ÖØ-öø-ÿ][A-Za-zÀ-ÖØ-öø-ÿ' ]{1,}$
   * @example Rossi
   */
  lastName: string
  /**
   * @pattern ^[A-Za-z]{6}[0-9LMNPQRSTUVlmnpqrstuv]{2}[AEBHCLDMPRSTabcdehlmprst]{1}[0-9LMNPQRSTUVlmnpqrstuv]{2}[A-Za-z]{1}[0-9LMNPQRSTUVlmnpqrstuv]{3}[A-Za-z]{1}$
   * @example RSSMRA04A03H501N
   */
  fiscalCode: string
  /**
   * @example true
   */
  taxDependent: boolean
  /**
   * Enumerazione dei gradi di parentela
   *
   * @example coniuge
   */
  relationship:
    | 'coniuge'
    | 'fratello_sorella'
    | 'figlio_figlia'
    | 'genero_nuora'
    | 'suocero_suocera'
    | 'genitore'
    | 'genitore_convivente'
    | 'genitore_percettore'
    | 'ascendente'
    | 'figlio_figlia_coniuge_deceduto'
    | 'discendente_prossimo'
  allowEditing?: boolean
  privacyDocument?: {
    documentId: string
    documentName: string
    status: 'created' | 'uploaded'
    urlPath: string
  }
}

export type UserFamilyMembersUpdateVariables = {
  body: UserFamilyMembersUpdateRequestBody
  headers?: UserFamilyMembersUpdateHeaders
  pathParams: UserFamilyMembersUpdatePathParams
} & MembershipContext['fetcherOptions']

/**
 * Update family member data
 */
export const fetchUserFamilyMembersUpdate = (variables: UserFamilyMembersUpdateVariables, signal?: AbortSignal) =>
  membershipFetch<
    undefined,
    UserFamilyMembersUpdateError,
    UserFamilyMembersUpdateRequestBody,
    UserFamilyMembersUpdateHeaders,
    {},
    UserFamilyMembersUpdatePathParams
  >({
    url: '/familyMembers/{familyMembersId}',
    method: 'put',
    ...variables,
    signal,
  })

/**
 * Update family member data
 */
export const useUserFamilyMembersUpdate = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, UserFamilyMembersUpdateError, UserFamilyMembersUpdateVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useMembershipContext()
  return reactQuery.useMutation<undefined, UserFamilyMembersUpdateError, UserFamilyMembersUpdateVariables>({
    mutationFn: (variables: UserFamilyMembersUpdateVariables) =>
      fetchUserFamilyMembersUpdate({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type UserFamilyMembersDeletePathParams = {
  /**
   * @maxLength 255
   */
  familyMembersId: string
}

export type UserFamilyMembersDeleteHeaders = {
  /**
   * The identifier of the tenant
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
  /**
   * The ID of the connected operator
   */
  ['X-Operator']?: string
}

export type UserFamilyMembersDeleteError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: Responses.ErrorBadRequest
    }
  | {
      status: 403
      payload: Responses.ErrorForbidden
    }
  | {
      status: 404
      payload: Responses.ErrorEntityNotFound
    }
  | {
      status: 409
      payload: Responses.ErrorConflict
    }
>

export type UserFamilyMembersDeleteVariables = {
  headers?: UserFamilyMembersDeleteHeaders
  pathParams: UserFamilyMembersDeletePathParams
} & MembershipContext['fetcherOptions']

/**
 * Delete family member data
 */
export const fetchUserFamilyMembersDelete = (variables: UserFamilyMembersDeleteVariables, signal?: AbortSignal) =>
  membershipFetch<
    undefined,
    UserFamilyMembersDeleteError,
    undefined,
    UserFamilyMembersDeleteHeaders,
    {},
    UserFamilyMembersDeletePathParams
  >({
    url: '/familyMembers/{familyMembersId}',
    method: 'delete',
    ...variables,
    signal,
  })

/**
 * Delete family member data
 */
export const useUserFamilyMembersDelete = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, UserFamilyMembersDeleteError, UserFamilyMembersDeleteVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useMembershipContext()
  return reactQuery.useMutation<undefined, UserFamilyMembersDeleteError, UserFamilyMembersDeleteVariables>({
    mutationFn: (variables: UserFamilyMembersDeleteVariables) =>
      fetchUserFamilyMembersDelete({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type UserFamilyMembersDocumentUploadPathParams = {
  /**
   * @maxLength 255
   */
  familyMembersId: string
}

export type UserFamilyMembersDocumentUploadHeaders = {
  /**
   * The identifier of the tenant
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
  /**
   * The ID of the connected operator
   */
  ['X-Operator']?: string
}

export type UserFamilyMembersDocumentUploadError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: Responses.ErrorBadRequest
    }
  | {
      status: 403
      payload: Responses.ErrorForbidden
    }
  | {
      status: 404
      payload: Responses.ErrorEntityNotFound
    }
  | {
      status: 409
      payload: Responses.ErrorConflict
    }
>

export type UserFamilyMembersDocumentUploadVariables = {
  body: Schemas.PrivacyDocumentUploadRequest
  headers?: UserFamilyMembersDocumentUploadHeaders
  pathParams: UserFamilyMembersDocumentUploadPathParams
} & MembershipContext['fetcherOptions']

/**
 * Get URL to upload document privacy
 */
export const fetchUserFamilyMembersDocumentUpload = (variables: UserFamilyMembersDocumentUploadVariables, signal?: AbortSignal) =>
  membershipFetch<
    Schemas.Document,
    UserFamilyMembersDocumentUploadError,
    Schemas.PrivacyDocumentUploadRequest,
    UserFamilyMembersDocumentUploadHeaders,
    {},
    UserFamilyMembersDocumentUploadPathParams
  >({
    url: '/familyMembers/{familyMembersId}/documents',
    method: 'post',
    ...variables,
    signal,
  })

/**
 * Get URL to upload document privacy
 */
export const useUserFamilyMembersDocumentUpload = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.Document,
      UserFamilyMembersDocumentUploadError,
      UserFamilyMembersDocumentUploadVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useMembershipContext()
  return reactQuery.useMutation<Schemas.Document, UserFamilyMembersDocumentUploadError, UserFamilyMembersDocumentUploadVariables>(
    {
      mutationFn: (variables: UserFamilyMembersDocumentUploadVariables) =>
        fetchUserFamilyMembersDocumentUpload({ ...fetcherOptions, ...variables }),
      ...options,
    },
  )
}

export type UserFamilyMembersDocumentGetPathParams = {
  /**
   * @maxLength 255
   */
  familyMembersId: string
  /**
   * @maxLength 36
   */
  documentId: string
}

export type UserFamilyMembersDocumentGetHeaders = {
  /**
   * The identifier of the tenant
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
  /**
   * The ID of the connected operator
   */
  ['X-Operator']?: string
}

export type UserFamilyMembersDocumentGetError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: Responses.ErrorBadRequest
    }
  | {
      status: 404
      payload: Responses.ErrorEntityNotFound
    }
>

export type UserFamilyMembersDocumentGetVariables = {
  headers?: UserFamilyMembersDocumentGetHeaders
  pathParams: UserFamilyMembersDocumentGetPathParams
} & MembershipContext['fetcherOptions']

/**
 * Get URL to download document privacy
 */
export const fetchUserFamilyMembersDocumentGet = (variables: UserFamilyMembersDocumentGetVariables, signal?: AbortSignal) =>
  membershipFetch<
    undefined,
    UserFamilyMembersDocumentGetError,
    undefined,
    UserFamilyMembersDocumentGetHeaders,
    {},
    UserFamilyMembersDocumentGetPathParams
  >({
    url: '/familyMembers/{familyMembersId}/documents/{documentId}',
    method: 'get',
    ...variables,
    signal,
  })

/**
 * Get URL to download document privacy
 */
export const useUserFamilyMembersDocumentGet = <TData = undefined>(
  variables: UserFamilyMembersDocumentGetVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<undefined, UserFamilyMembersDocumentGetError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useMembershipContext(options)
  return reactQuery.useQuery<undefined, UserFamilyMembersDocumentGetError, TData>({
    queryKey: queryKeyFn({
      path: '/familyMembers/{familyMembersId}/documents/{documentId}',
      operationId: 'userFamilyMembersDocumentGet',
      variables,
    }),
    queryFn: ({ signal }) => fetchUserFamilyMembersDocumentGet({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  })
}

export type UserFamilyMembersDocumentDeletePathParams = {
  /**
   * @maxLength 255
   */
  familyMembersId: string
  /**
   * @maxLength 36
   */
  documentId: string
}

export type UserFamilyMembersDocumentDeleteHeaders = {
  /**
   * The identifier of the tenant
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
  /**
   * The ID of the connected operator
   */
  ['X-Operator']?: string
}

export type UserFamilyMembersDocumentDeleteError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: Responses.ErrorBadRequest
    }
  | {
      status: 404
      payload: Responses.ErrorEntityNotFound
    }
>

export type UserFamilyMembersDocumentDeleteVariables = {
  headers?: UserFamilyMembersDocumentDeleteHeaders
  pathParams: UserFamilyMembersDocumentDeletePathParams
} & MembershipContext['fetcherOptions']

/**
 * Delete document privacy
 */
export const fetchUserFamilyMembersDocumentDelete = (variables: UserFamilyMembersDocumentDeleteVariables, signal?: AbortSignal) =>
  membershipFetch<
    undefined,
    UserFamilyMembersDocumentDeleteError,
    undefined,
    UserFamilyMembersDocumentDeleteHeaders,
    {},
    UserFamilyMembersDocumentDeletePathParams
  >({
    url: '/familyMembers/{familyMembersId}/documents/{documentId}',
    method: 'delete',
    ...variables,
    signal,
  })

/**
 * Delete document privacy
 */
export const useUserFamilyMembersDocumentDelete = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, UserFamilyMembersDocumentDeleteError, UserFamilyMembersDocumentDeleteVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useMembershipContext()
  return reactQuery.useMutation<undefined, UserFamilyMembersDocumentDeleteError, UserFamilyMembersDocumentDeleteVariables>({
    mutationFn: (variables: UserFamilyMembersDocumentDeleteVariables) =>
      fetchUserFamilyMembersDocumentDelete({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type UserFamilyMembersDocumentUpdatePathParams = {
  /**
   * @maxLength 255
   */
  familyMembersId: string
  /**
   * @maxLength 36
   */
  documentId: string
}

export type UserFamilyMembersDocumentUpdateHeaders = {
  /**
   * The identifier of the tenant
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
  /**
   * The ID of the connected operator
   */
  ['X-Operator']?: string
}

export type UserFamilyMembersDocumentUpdateError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: Responses.ErrorBadRequest
    }
  | {
      status: 404
      payload: Responses.ErrorEntityNotFound
    }
>

export type UserFamilyMembersDocumentUpdateVariables = {
  body: Schemas.PrivacyDocumentUpdateRequest
  headers?: UserFamilyMembersDocumentUpdateHeaders
  pathParams: UserFamilyMembersDocumentUpdatePathParams
} & MembershipContext['fetcherOptions']

/**
 * Update status document privacy
 */
export const fetchUserFamilyMembersDocumentUpdate = (variables: UserFamilyMembersDocumentUpdateVariables, signal?: AbortSignal) =>
  membershipFetch<
    undefined,
    UserFamilyMembersDocumentUpdateError,
    Schemas.PrivacyDocumentUpdateRequest,
    UserFamilyMembersDocumentUpdateHeaders,
    {},
    UserFamilyMembersDocumentUpdatePathParams
  >({
    url: '/familyMembers/{familyMembersId}/documents/{documentId}',
    method: 'put',
    ...variables,
    signal,
  })

/**
 * Update status document privacy
 */
export const useUserFamilyMembersDocumentUpdate = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, UserFamilyMembersDocumentUpdateError, UserFamilyMembersDocumentUpdateVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useMembershipContext()
  return reactQuery.useMutation<undefined, UserFamilyMembersDocumentUpdateError, UserFamilyMembersDocumentUpdateVariables>({
    mutationFn: (variables: UserFamilyMembersDocumentUpdateVariables) =>
      fetchUserFamilyMembersDocumentUpdate({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type UsersValidateFiscalCodeError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: Responses.ErrorBadRequest
    }
  | {
      status: 404
      payload: Responses.ErrorEntityNotFound
    }
>

export type UsersValidateFiscalCodeVariables = {
  body?: Schemas.ValidateFiscalCodeRequest
} & MembershipContext['fetcherOptions']

/**
 * Validate fiscalCode and extract birth date
 */
export const fetchUsersValidateFiscalCode = (variables: UsersValidateFiscalCodeVariables, signal?: AbortSignal) =>
  membershipFetch<
    Schemas.ValidateFiscalCodeResponse,
    UsersValidateFiscalCodeError,
    Schemas.ValidateFiscalCodeRequest,
    {},
    {},
    {}
  >({ url: '/fiscalCode/validate', method: 'post', ...variables, signal })

/**
 * Validate fiscalCode and extract birth date
 */
export const useUsersValidateFiscalCode = (
  options?: Omit<
    reactQuery.UseMutationOptions<
      Schemas.ValidateFiscalCodeResponse,
      UsersValidateFiscalCodeError,
      UsersValidateFiscalCodeVariables
    >,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useMembershipContext()
  return reactQuery.useMutation<
    Schemas.ValidateFiscalCodeResponse,
    UsersValidateFiscalCodeError,
    UsersValidateFiscalCodeVariables
  >({
    mutationFn: (variables: UsersValidateFiscalCodeVariables) =>
      fetchUsersValidateFiscalCode({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type DocumentAcknowledgePathParams = {
  /**
   * @maxLength 24
   */
  documentId: string
}

export type DocumentAcknowledgeHeaders = {
  /**
   * The identifier of the tenant
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
  ['X-CompanyCode']?: string
  ['X-CampaignID']?: string
}

export type DocumentAcknowledgeError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: Responses.ErrorBadRequest
    }
  | {
      status: 404
      payload: Responses.ErrorEntityNotFound
    }
>

export type DocumentAcknowledgeVariables = {
  body?: Schemas.DocumentAcknowledgeRequest
  headers?: DocumentAcknowledgeHeaders
  pathParams: DocumentAcknowledgePathParams
} & MembershipContext['fetcherOptions']

/**
 * Records that the authenticated user has viewed the specified document version. Both "not found" and "outside visibility scope" return 404.
 */
export const fetchDocumentAcknowledge = (variables: DocumentAcknowledgeVariables, signal?: AbortSignal) =>
  membershipFetch<
    undefined,
    DocumentAcknowledgeError,
    Schemas.DocumentAcknowledgeRequest,
    DocumentAcknowledgeHeaders,
    {},
    DocumentAcknowledgePathParams
  >({
    url: '/documents/{documentId}/acknowledge',
    method: 'post',
    ...variables,
    signal,
  })

/**
 * Records that the authenticated user has viewed the specified document version. Both "not found" and "outside visibility scope" return 404.
 */
export const useDocumentAcknowledge = (
  options?: Omit<reactQuery.UseMutationOptions<undefined, DocumentAcknowledgeError, DocumentAcknowledgeVariables>, 'mutationFn'>,
) => {
  const { fetcherOptions } = useMembershipContext()
  return reactQuery.useMutation<undefined, DocumentAcknowledgeError, DocumentAcknowledgeVariables>({
    mutationFn: (variables: DocumentAcknowledgeVariables) => fetchDocumentAcknowledge({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type QueryOperation =
  | {
      path: '/memberships/{membershipId}'
      operationId: 'membershipGet'
      variables: MembershipGetVariables
    }
  | {
      path: '/memberships/users/{userId}'
      operationId: 'userMembershipList'
      variables: UserMembershipListVariables
    }
  | {
      path: '/memberships/users/{userId}/active'
      operationId: 'userMembershipActiveGet'
      variables: UserMembershipActiveGetVariables
    }
  | {
      path: '/memberships/{membershipId}/familyMembers'
      operationId: 'userFamilyMembersGet'
      variables: UserFamilyMembersGetVariables
    }
  | {
      path: '/users/check'
      operationId: 'userCheck'
      variables: UserCheckVariables
    }
  | {
      path: '/users/info'
      operationId: 'userInfo'
      variables: UserInfoVariables
    }
  | {
      path: '/users/self-registration-configuration'
      operationId: 'userSelfRegistrationConfig'
      variables: UserSelfRegistrationConfigVariables
    }
  | {
      path: '/familyMembers/{familyMembersId}/documents/{documentId}'
      operationId: 'userFamilyMembersDocumentGet'
      variables: UserFamilyMembersDocumentGetVariables
    }
